import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, SlidersHorizontal, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * scope: "primary" (Classes 1–10, default) | "pre_primary" (Nursery/LKG/UKG).
 * Pre-primary rules can be saved empty and don't require a pass mark
 * (age-appropriate — everyone passes).
 */
export default function GradingConfigDialog({ isOpen, onClose, scope = "primary" }) {
  const isPP = scope === "pre_primary";
  const endpoint = isPP ? "/admin/grading-config/pre-primary" : "/admin/grading-config";
  const title = isPP ? "Pre-Primary grading rules" : "Grading rules";
  const description = isPP
    ? "Nursery, LKG & UKG. Define your own grade labels and ranges. No pass mark — everyone passes."
    : "Define the grade for each percentage range and the per-subject pass mark. Used on report cards.";

  const [bands, setBands] = useState([]);
  const [failGrade, setFailGrade] = useState(isPP ? "" : "D (Fail)");
  const [passPct, setPassPct] = useState(isPP ? 0 : 35);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    api.get(endpoint)
      .then(({ data }) => {
        setBands(data.bands || []);
        setFailGrade(data.fail_grade ?? (isPP ? "" : "D (Fail)"));
        setPassPct(data.pass_pct ?? (isPP ? 0 : 35));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isOpen, endpoint, isPP]);

  const updateBand = (i, key, val) => setBands((b) => b.map((x, j) => (j === i ? { ...x, [key]: val } : x)));
  const addBand = () => setBands((b) => [...b, { min: 0, grade: "" }]);
  const removeBand = (i) => setBands((b) => b.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = bands
        .filter((b) => b.grade && b.grade.trim())
        .map((b) => ({ min: Number(b.min), grade: b.grade.trim() }))
        .sort((a, z) => z.min - a.min);
      if (!isPP && !cleaned.length) { toast.error("Add at least one grade band"); setSaving(false); return; }
      const payload = {
        bands: cleaned,
        fail_grade: (failGrade || "").trim() || (isPP ? "" : "D (Fail)"),
        pass_pct: Number(passPct) || 0,
      };
      await api.put(endpoint, payload);
      toast.success(isPP ? "Pre-primary grading rules saved" : "Grading rules saved");
      onClose();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid={isPP ? "grading-config-pp-dialog" : "grading-config-dialog"}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}><SlidersHorizontal className="inline h-5 w-5 mr-1.5 text-indigo-500" />{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-semibold text-slate-500 mb-1.5 px-1">
                <span>Min %</span><span>Grade</span><span></span>
              </div>
              <div className="space-y-2" data-testid="grade-bands">
                {bands.length === 0 && (
                  <p className="text-xs text-slate-400 italic px-1 py-2">
                    {isPP ? "No bands yet — add your first pre-primary grade below." : "No bands yet — add one below."}
                  </p>
                )}
                {bands.map((b, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                    <input type="number" value={b.min} onChange={(e) => updateBand(i, "min", e.target.value)} className="h-9 rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-2 text-sm" data-testid={`band-min-${i}`} />
                    <input value={b.grade} onChange={(e) => updateBand(i, "grade", e.target.value)} placeholder={isPP ? "e.g. Excellent" : ""} className="h-9 rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-2 text-sm" data-testid={`band-grade-${i}`} />
                    <button onClick={() => removeBand(i)} className="h-9 w-9 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded-lg" data-testid={`band-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addBand} className="mt-2 rounded-lg h-8 text-xs" data-testid="add-band-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add band</Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Below-min grade {isPP && <span className="text-slate-400 font-normal">(optional)</span>}</label>
                <input value={failGrade} onChange={(e) => setFailGrade(e.target.value)} className="h-9 w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-2 text-sm" data-testid="fail-grade-input" />
              </div>
              {!isPP && (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Pass mark (% per subject)</label>
                  <input type="number" value={passPct} onChange={(e) => setPassPct(e.target.value)} className="h-9 w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-2 text-sm" data-testid="pass-pct-input" />
                </div>
              )}
            </div>
            {isPP && (
              <p className="text-xs text-slate-500 dark:text-slate-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg px-3 py-2">
                <span className="font-semibold text-amber-700 dark:text-amber-300">Note:</span> These rules apply only to <span className="font-semibold">Nursery, LKG & UKG</span>. If left empty, pre-primary reports will fall back to the primary grading rules.
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={isPP ? "save-grading-pp-btn" : "save-grading-btn"}>
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Save rules
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
